/**
 * PlacementHud — placement-phase controls. Shows armies remaining, the
 * per-tap multiplier (so the player can drop troops by 1, 5, or all
 * remaining at a time), and Reset / Commit buttons. Tapping a
 * territory on the map adds the current per-tap amount (clamped to
 * `remaining`); Reset clears the pending placements; Commit fires the
 * API call once the budget is exhausted.
 *
 * An optional `cardChip` slot pins a small element (the cards HUD chip)
 * to the HUD's top-right corner so it visually sits on the HUD's
 * border — placement is the only phase where cards can be turned in,
 * so the chip lives with the placement controls rather than floating
 * at screen level.
 */

import React from 'react'
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native'
import { TurnControlCard } from '../turn/TurnControlCard'
import { TURN_CARD_SPACING } from '../../lib/turnPanelAppearance'

export type PlacementStepMode = 1 | 5 | 'max'

interface PlacementHudProps {
  remaining: number
  budget: number
  onReset: () => void
  onUndo: () => void
  onCommit: () => void
  canReset: boolean
  /** True when there is at least one action to undo. */
  canUndo: boolean
  canCommit: boolean
  isCommitting: boolean
  /** Current per-tap amount. */
  stepMode: PlacementStepMode
  onStepModeChange: (mode: PlacementStepMode) => void
  /**
   * Optional bonus-armies subtitle. When non-zero we surface where the
   * extra placement supply came from (card turn-ins + eliminate-player
   * bonus) so the player understands why their budget jumped.
   */
  /**
   * The "// includes ..." bonus subtitle (built by placementBonusText). When
   * null/empty the subtitle is hidden. Names each source, e.g.
   * '+5 elimination bonus, +8 card bonus'.
   */
  bonusText?: string | null
  /**
   * Optional decoration pinned to the HUD's top-right corner. Used to
   * dock the cards HUD chip so it visually rides on the HUD border.
   * Sits in an absolutely-positioned slot so it doesn't displace the
   * "remaining / budget" line.
   */
  cardChip?: React.ReactNode
}

export function PlacementHud({
  remaining,
  budget,
  onReset,
  onUndo,
  onCommit,
  canReset,
  canUndo,
  canCommit,
  isCommitting,
  stepMode,
  onStepModeChange,
  bonusText,
  cardChip,
}: PlacementHudProps) {
  return (
    <TurnControlCard
      accessibilityLabel="Placement controls"
      testID="turn-control-card"
      style={{ position: 'relative', gap: TURN_CARD_SPACING.placementGap }}
    >
      <View className="flex-row items-baseline gap-1">
        <Text
          // text-olive-up reads well on the canvas-tinted background in
          // both themes; switches to ink when the budget is exhausted —
          // the "ready to commit" signal.
          className={`text-2xl font-stencil ${remaining > 0 ? 'text-olive-up' : 'text-ink'}`}
          accessibilityLabel={`${budget - remaining} of ${budget} armies placed`}
        >
          {budget - remaining}
        </Text>
        <Text className="text-ink-soft text-xs font-typed">/ {budget} placed</Text>
      </View>
      {bonusText ? (
        // Subtle subtitle so the player sees WHY their budget jumped, broken
        // down by source (elimination / card turn-in). See placementBonusText.
        <Text className="text-blood text-[10px] font-typed italic">
          // includes {bonusText}
        </Text>
      ) : null}
      <View className="flex-row items-center gap-1.5">
        <Text className="text-ink-soft text-[10px] font-typed">per tap</Text>
        {([1, 5, 'max'] as const).map((mode) => {
          const isActive = stepMode === mode
          const label = mode === 'max' ? 'Max' : `+${mode}`
          return (
            <TouchableOpacity
              key={mode}
              onPress={() => onStepModeChange(mode)}
              accessibilityLabel={`Place ${label} per tap`}
              accessibilityRole="button"
              // Olive border on every pill — bg-paper-up alone barely
              // contrasts with the canvas-tinted hud background in
              // light mode.
              className={`px-2 py-1 rounded-md border ${
                isActive ? 'bg-blood border-blood' : 'bg-paper border-olive'
              }`}
              style={{ minHeight: 28, minWidth: 36, justifyContent: 'center', alignItems: 'center' }}
            >
              <Text
                className={`font-stencil text-[11px] uppercase ${
                  isActive ? 'text-white' : 'text-ink'
                }`}
              >
                {label}
              </Text>
            </TouchableOpacity>
          )
        })}
      </View>
      <View testID="placement-actions" className="flex-row gap-2" style={{ marginTop: TURN_CARD_SPACING.placementActionsTop }}>
        <TouchableOpacity
          onPress={onReset}
          disabled={!canReset}
          accessibilityLabel="Reset placements"
          accessibilityRole="button"
          className="flex-1 bg-paper border border-olive rounded-md px-2 py-1.5 items-center justify-center"
          style={{ opacity: canReset ? 1 : 0.4, minHeight: 32 }}
        >
          <Text className="text-ink text-xs font-stencil uppercase">Reset</Text>
        </TouchableOpacity>
        {/* Undo pops only the most-recent placement step from the stack.
            Disabled when the stack is empty (nothing staged yet). */}
        <TouchableOpacity
          onPress={onUndo}
          disabled={!canUndo}
          accessibilityLabel="Undo last placement"
          accessibilityRole="button"
          className="flex-1 bg-paper border border-olive rounded-md px-2 py-1.5 items-center justify-center"
          style={{ opacity: canUndo ? 1 : 0.4, minHeight: 32 }}
        >
          <Text className="text-ink text-xs font-stencil uppercase">↶ Undo</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={onCommit}
          disabled={!canCommit}
          accessibilityLabel="Commit placements"
          accessibilityRole="button"
          className="flex-1 bg-blood rounded-md px-2 py-1.5 items-center justify-center flex-row gap-1"
          style={{ opacity: canCommit ? 1 : 0.4, minHeight: 32 }}
        >
          {isCommitting && <ActivityIndicator size="small" color="white" />}
          <Text className="text-white text-xs font-stencil uppercase">Commit</Text>
        </TouchableOpacity>
      </View>
      {/* Card chip — pinned inside the HUD's top-right corner with the
          same padding as the rest of the contents, so it reads as part
          of the box rather than floating on its border. */}
      {cardChip && (
        <View
          pointerEvents="box-none"
          style={{
            position: 'absolute',
            // 12px in: the right edge lines up with Commit (the HUD's px-3),
            // leaving the count bubble clear of the HUD's border.
            top: 12,
            right: 12,
          }}
        >
          {cardChip}
        </View>
      )}
    </TurnControlCard>
  )
}
